import { authorizedFetch } from '@yeshuman/sdk'
import { useQuery } from '@tanstack/react-query'
import type { HealthRecordItem, HealthRecordType } from './use-health-records'

export interface HealthRecordSearchResult extends HealthRecordItem {
  similarity_score: number
  match_type: 'semantic' | 'keyword' | 'both'
  match_reasons: string[]
  matched_fields: string[]
}

export interface HealthRecordSearchResponse {
  results: HealthRecordSearchResult[]
  total: number
  query: string
}

export interface HealthRecordSearchFilters {
  record_type?: HealthRecordType
  classification_status?: string
  source?: string
  limit?: number
}

export function useHealthRecordSearch(
  query: string,
  enabled: boolean = true,
  filters: HealthRecordSearchFilters = {},
) {
  const trimmed = query.trim()

  const { data, isLoading, error, isFetching } = useQuery<HealthRecordSearchResponse>({
    queryKey: ['health-records', 'search', trimmed, filters],
    queryFn: async () => {
      if (!trimmed) {
        return { results: [], total: 0, query: '' }
      }

      const params = new URLSearchParams({ q: trimmed })
      if (filters.record_type) params.set('record_type', filters.record_type)
      if (filters.classification_status) params.set('classification_status', filters.classification_status)
      if (filters.source) params.set('source', filters.source)
      if (filters.limit) params.set('limit', String(filters.limit))

      const response = await authorizedFetch(`/api/health-records/search?${params}`)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to search health records')
      }
      return response.json()
    },
    enabled: enabled && trimmed.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })

  return {
    results: data?.results || [],
    total: data?.total || 0,
    query: data?.query || '',
    isLoading: isLoading || isFetching,
    error,
  }
}
